import { Config } from "../../Config";
import { FaceTecSDK } from "../../core-sdk/FaceTecSDK.js/FaceTecSDK";
import { SampleAppUtilities } from "./utilities/SampleAppUtilities";
import { ThemeHelpers } from "./utilities/ThemeHelpers";
import { FaceTecInitializationError, FaceTecSessionResult, type FaceTecSDKInstance } from "../../core-sdk/FaceTecSDK.js/FaceTecPublicApi";
import { DeveloperStatusMessages } from "./utilities/DeveloperStatusMessages";
import { SessionRequestProcessor } from "./SessionRequestProcessor";
import { WizardState, WizardStep } from "./WizardState";
import { removeBackground } from "@imgly/background-removal";

export class SampleAppController {
  private static instance: SampleAppController;
  private themeHelpers: ThemeHelpers = new ThemeHelpers();
  private faceTecSDKInstance!: FaceTecSDKInstance;
  private wizardState: WizardState = new WizardState();
  private cameraStream: MediaStream | null = null;
  private capturedPhotoDataUrl: string = "";
  private originalPhotoDataUrl: string = "";
  private processedPhotoDataUrl: string = "";

  public static latestOfficialIDPhoto: string = "";
  public static demonstrationExternalDatabaseRefID: string = "";

  // Map wizard steps to their HTML element IDs
  private static readonly STEP_ELEMENTS: Record<number, string> = {
    [WizardStep.WELCOME]: "welcome-screen",
    [WizardStep.PORTRAIT_PHOTO]: "step-portrait",
    [WizardStep.FACETEC_VERIFICATION]: "step-facetec",
    [WizardStep.APPLICATION_FORM]: "step-application-form",
    [WizardStep.CONFIRMATION]: "step-confirmation"
  };

  public constructor() {
    SampleAppController.instance = this;
    SampleAppUtilities.formatUIForDevice();
    this.setupPortraitHandlers();
    this.initializeFaceTecSDK();
  }

  private initializeFaceTecSDK = (): void => {
    FaceTecSDK.setResourceDirectory("../core-sdk/FaceTecSDK.js/resources");
    FaceTecSDK.setImagesDirectory("../core-sdk/FaceTec_images");

    FaceTecSDK.initializeWithSessionRequest(Config.DeviceKeyIdentifier, new SessionRequestProcessor(), {
      onSuccess: (newFaceTecSdkInstance: FaceTecSDKInstance) => {
        this.faceTecSDKInstance = newFaceTecSdkInstance;
        this.onFaceTecSDKInitializationSuccess();
      },
      onError: (initializationError: FaceTecInitializationError) => {
        this.onFaceTecSDKInitializationFailure(initializationError);
      }
    });
  };

  private onFaceTecSDKInitializationSuccess = (): void => {
    const startBtn = document.getElementById("start-application-button");
    if (startBtn) {
      startBtn.removeAttribute("disabled");
    }

    document.body.style.transition = "opacity 0.6s ease";
    document.body.style.opacity = "1";

    this.themeHelpers.setAppTheme(this.themeHelpers.getCurrentTheme());
    SampleAppUtilities.setVocalGuidanceSoundFiles();
    SampleAppUtilities.setOCRLocalization();

    this.updateStatus("Ready to start your application.");
  };

  private onFaceTecSDKInitializationFailure = (initializationError: FaceTecInitializationError): void => {
    document.body.style.transition = "opacity 0.6s ease";
    document.body.style.opacity = "1";

    DeveloperStatusMessages.logInitializationErrorResult(initializationError);
    this.updateStatus("Initialization failed. Please refresh.");
  };

  private updateStatus(message: string): void {
    const statusEl = document.getElementById("status");
    if (statusEl) {
      statusEl.textContent = message;
    }
  }

  // =============================================
  // WIZARD NAVIGATION
  // =============================================

  private transitionToStep(targetStep: WizardStep): void {
    const currentEl = document.getElementById(SampleAppController.STEP_ELEMENTS[this.wizardState.currentStep]);
    const targetEl = document.getElementById(SampleAppController.STEP_ELEMENTS[targetStep]);
    const progressBar = document.getElementById("wizard-progress");

    // Fade out current screen
    if (currentEl) {
      currentEl.style.transition = "opacity 0.4s ease";
      currentEl.style.opacity = "0";
      setTimeout(() => { currentEl.style.display = "none"; }, 400);
    }

    // Show/hide progress bar
    if (progressBar) {
      if (targetStep === WizardStep.WELCOME) {
        progressBar.style.display = "none";
      } else {
        progressBar.style.display = "block";
      }
    }

    // Update progress indicator
    this.updateProgressIndicator(targetStep);

    // Fade in target screen after current fades out
    setTimeout(() => {
      if (targetEl) {
        targetEl.style.display = "flex";
        targetEl.style.opacity = "0";
        requestAnimationFrame(() => {
          targetEl.style.transition = "opacity 0.4s ease";
          targetEl.style.opacity = "1";
        });
      }
    }, 420);

    this.wizardState.currentStep = targetStep;
  }

  private updateProgressIndicator(currentStep: WizardStep): void {
    const steps = document.querySelectorAll(".wizard-progress-step");
    const connectors = document.querySelectorAll(".wizard-progress-connector");

    steps.forEach((stepEl) => {
      const stepNum = parseInt(stepEl.getAttribute("data-step") || "0", 10);
      stepEl.classList.remove("wizard-progress-step--active", "wizard-progress-step--completed");

      if (stepNum === currentStep) {
        stepEl.classList.add("wizard-progress-step--active");
      } else if (stepNum < currentStep) {
        stepEl.classList.add("wizard-progress-step--completed");
      }
    });

    connectors.forEach((connEl) => {
      const afterStep = parseInt(connEl.getAttribute("data-after") || "0", 10);
      connEl.classList.remove("wizard-progress-connector--completed");

      if (afterStep < currentStep) {
        connEl.classList.add("wizard-progress-connector--completed");
      }
    });
  }

  // Welcome -> Step 1
  public onStartApplicationPressed = (): void => {
    this.transitionToStep(WizardStep.PORTRAIT_PHOTO);
  };

  // Step 1 -> Welcome
  public onPortraitBack = (): void => {
    this.stopCameraStream();
    this.transitionToStep(WizardStep.WELCOME);
  };

  // Step 1 -> Step 2
  public onPortraitNext = (): void => {
    if (!this.wizardState.portrait) {
      return;
    }
    this.stopCameraStream();
    this.transitionToStep(WizardStep.FACETEC_VERIFICATION);
  };

  // Step 2 -> Step 1
  public onFaceTecBack = (): void => {
    this.transitionToStep(WizardStep.PORTRAIT_PHOTO);
  };

  // Step 2: Launch FaceTec
  public onFaceTecBeginVerification = (): void => {
    const stepEl = document.getElementById("step-facetec");

    // Fade out step 2 screen (progress bar stays visible during FaceTec scan)
    if (stepEl) {
      stepEl.style.transition = "opacity 0.4s ease";
      stepEl.style.opacity = "0";
      setTimeout(() => { stepEl.style.display = "none"; }, 400);
    }

    SampleAppUtilities.fadeOutMainUIAndPrepareForSession();

    SampleAppController.demonstrationExternalDatabaseRefID =
      "browser_sample_app_" + SampleAppUtilities.generateUUId();

    this.faceTecSDKInstance.start3DLivenessThen3D2DPhotoIDMatch(new SessionRequestProcessor());
  };

  // Step 3 -> Step 2
  public onFormBack = (): void => {
    this.transitionToStep(WizardStep.FACETEC_VERIFICATION);
  };

  // Step 3: Submit form
  public onFormSubmit = (): void => {
    if (!this.validateForm()) {
      return;
    }
    this.collectFormData();
    this.submitApplication();
  };

  // FaceTec exit handler - called by SessionRequestProcessor
  public static demonstrateHandlingFaceTecExit = (FaceTecSessionResult: FaceTecSessionResult): void => {
    DeveloperStatusMessages.logSessionStatusOnFaceTecExit(FaceTecSessionResult.status);

    const instance = SampleAppController.instance;

    if (FaceTecSessionResult.status === FaceTecSDK.FaceTecSessionStatus.SessionCompleted) {
      // Success -> go to Step 3
      instance.wizardState.faceTecCompleted = true;
      instance.transitionToStep(WizardStep.APPLICATION_FORM);
    } else {
      // Failure -> stay on Step 2 with error message
      SampleAppController.demonstrationExternalDatabaseRefID = "";
      SampleAppController.latestOfficialIDPhoto = "";

      instance.transitionToStep(WizardStep.FACETEC_VERIFICATION);

      // Show error after transition
      setTimeout(() => {
        const statusEl = document.getElementById("facetec-step-status");
        if (statusEl) {
          statusEl.textContent = "Verification was not completed. Please try again.";
          statusEl.style.display = "block";
        }
      }, 500);
    }

    SampleAppUtilities.showMainUI();
  };

  // =============================================
  // PORTRAIT PHOTO HANDLING
  // =============================================

  private setupPortraitHandlers(): void {
    // Wait for DOM
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () => this.attachPortraitListeners());
    } else {
      this.attachPortraitListeners();
    }
  }

  private attachPortraitListeners(): void {
    const fileInput = document.getElementById("portrait-file-input") as HTMLInputElement | null;
    const dropzone = document.getElementById("portrait-dropzone");

    if (fileInput) {
      fileInput.addEventListener("change", () => {
        if (fileInput.files && fileInput.files.length > 0) {
          this.handlePortraitFile(fileInput.files[0]);
        }
      });
    }

    if (dropzone) {
      dropzone.addEventListener("dragover", (e: DragEvent) => {
        e.preventDefault();
        dropzone.classList.add("portrait-dropzone--dragover");
      });

      dropzone.addEventListener("dragleave", () => {
        dropzone.classList.remove("portrait-dropzone--dragover");
      });

      dropzone.addEventListener("drop", (e: DragEvent) => {
        e.preventDefault();
        dropzone.classList.remove("portrait-dropzone--dragover");
        if (e.dataTransfer && e.dataTransfer.files.length > 0) {
          this.handlePortraitFile(e.dataTransfer.files[0]);
        }
      });
    }
  }

  // Show mode selection
  public showPortraitModeSelect = (): void => {
    this.hideAllPortraitModes();
    const modeSelect = document.getElementById("portrait-mode-select");
    if (modeSelect) modeSelect.style.display = "block";
  };

  // Take photo selected
  public onTakePhotoSelected = (): void => {
    this.hideAllPortraitModes();
    const cameraMode = document.getElementById("portrait-camera-mode");
    if (cameraMode) cameraMode.style.display = "block";
    this.startCamera();
  };

  // Upload photo selected
  public onUploadPhotoSelected = (): void => {
    this.hideAllPortraitModes();
    const uploadMode = document.getElementById("portrait-upload-mode");
    if (uploadMode) uploadMode.style.display = "block";
  };

  private hideAllPortraitModes(): void {
    const ids = ["portrait-mode-select", "portrait-upload-mode", "portrait-camera-mode", "portrait-final-preview"];
    ids.forEach(id => {
      const el = document.getElementById(id);
      if (el) el.style.display = "none";
    });
  }

  // Camera methods
  private startCamera(): void {
    const video = document.getElementById("camera-video") as HTMLVideoElement | null;
    const liveView = document.getElementById("camera-live-view");
    const previewView = document.getElementById("camera-preview-view");

    if (liveView) liveView.style.display = "block";
    if (previewView) previewView.style.display = "none";

    if (!video) return;

    navigator.mediaDevices.getUserMedia({
      video: { facingMode: "user", width: { ideal: 480 }, height: { ideal: 640 } }
    }).then((stream) => {
      this.cameraStream = stream;
      video.srcObject = stream;
    }).catch((_err) => {
      this.showPortraitError("Unable to access camera. Please check permissions or use the upload option.");
      this.showPortraitModeSelect();
    });
  }

  public onCameraCapture = (): void => {
    const video = document.getElementById("camera-video") as HTMLVideoElement | null;
    const canvas = document.getElementById("camera-canvas") as HTMLCanvasElement | null;
    const liveView = document.getElementById("camera-live-view");
    const previewView = document.getElementById("camera-preview-view");

    if (!video || !canvas) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Mirror the capture to match the mirrored video display
    ctx.translate(canvas.width, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(video, 0, 0);
    ctx.setTransform(1, 0, 0, 1, 0, 0);

    // Crop original to 3:4 portrait
    this.originalPhotoDataUrl = this.cropToPortrait(canvas);
    this.capturedPhotoDataUrl = this.originalPhotoDataUrl;

    // Show captured preview immediately (no processing)
    if (liveView) liveView.style.display = "none";
    if (previewView) previewView.style.display = "block";

    const capturedPreview = document.getElementById("camera-captured-preview");
    const capturedImg = document.getElementById("camera-captured-img") as HTMLImageElement | null;
    const choiceUI = document.getElementById("camera-photo-choice");
    const processingOverlay = document.getElementById("camera-processing-overlay");

    if (capturedPreview) capturedPreview.style.display = "block";
    if (capturedImg) capturedImg.src = this.originalPhotoDataUrl;
    if (choiceUI) choiceUI.style.display = "none";
    if (processingOverlay) processingOverlay.style.display = "none";
  };

  public onRemoveBackground = (): void => {
    const capturedPreview = document.getElementById("camera-captured-preview");
    const processingOverlay = document.getElementById("camera-processing-overlay");
    const removeBgBtn = document.getElementById("remove-bg-btn") as HTMLButtonElement | null;

    if (capturedPreview) capturedPreview.style.display = "none";
    if (processingOverlay) processingOverlay.style.display = "flex";
    if (removeBgBtn) removeBgBtn.setAttribute("disabled", "true");

    const canvas = document.getElementById("camera-canvas") as HTMLCanvasElement | null;
    if (!canvas) return;

    canvas.toBlob((blob) => {
      if (!blob) {
        // Failed to create blob, go back to captured preview
        if (capturedPreview) capturedPreview.style.display = "block";
        if (processingOverlay) processingOverlay.style.display = "none";
        if (removeBgBtn) removeBgBtn.removeAttribute("disabled");
        return;
      }

      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error("timeout")), 30000)
      );

      Promise.race([this.processPortraitPhoto(blob), timeoutPromise])
        .then((dataUrl) => {
          this.processedPhotoDataUrl = dataUrl;
          this.showPhotoChoice(this.originalPhotoDataUrl, dataUrl);
        })
        .catch(() => {
          // Background removal failed, go back to captured preview
          if (capturedPreview) capturedPreview.style.display = "block";
          if (processingOverlay) processingOverlay.style.display = "none";
          if (removeBgBtn) removeBgBtn.removeAttribute("disabled");
        });
    }, "image/jpeg", 0.92);
  };

  private cropToPortrait(canvas: HTMLCanvasElement): string {
    const targetRatio = 3 / 4;
    const srcRatio = canvas.width / canvas.height;

    let cropW: number, cropH: number, cropX: number, cropY: number;
    if (srcRatio > targetRatio) {
      cropH = canvas.height;
      cropW = Math.round(canvas.height * targetRatio);
      cropX = Math.round((canvas.width - cropW) / 2);
      cropY = 0;
    } else {
      cropW = canvas.width;
      cropH = Math.round(canvas.width / targetRatio);
      cropX = 0;
      cropY = Math.round((canvas.height - cropH) / 2);
    }

    const cropCanvas = document.createElement("canvas");
    cropCanvas.width = cropW;
    cropCanvas.height = cropH;
    const ctx = cropCanvas.getContext("2d")!;
    ctx.drawImage(canvas, cropX, cropY, cropW, cropH, 0, 0, cropW, cropH);

    return cropCanvas.toDataURL("image/jpeg", 0.92);
  }

  private showPhotoChoice(originalUrl: string, processedUrl: string | null): void {
    const processingOverlay = document.getElementById("camera-processing-overlay");
    const capturedPreview = document.getElementById("camera-captured-preview");
    const choiceUI = document.getElementById("camera-photo-choice");
    const originalImg = document.getElementById("choice-original-img") as HTMLImageElement | null;
    const processedImg = document.getElementById("choice-processed-img") as HTMLImageElement | null;
    const processedOption = document.getElementById("choice-processed");

    if (processingOverlay) processingOverlay.style.display = "none";
    if (capturedPreview) capturedPreview.style.display = "none";

    if (originalImg) originalImg.src = originalUrl;

    if (processedUrl) {
      if (processedImg) processedImg.src = processedUrl;
      if (processedOption) processedOption.style.display = "flex";
    } else {
      if (processedOption) processedOption.style.display = "none";
    }

    // Reset selection state
    document.getElementById("choice-original")?.classList.remove("camera-choice-option--selected");
    document.getElementById("choice-processed")?.classList.remove("camera-choice-option--selected");
    this.capturedPhotoDataUrl = "";

    if (choiceUI) choiceUI.style.display = "block";
  }

  public onPhotoChoiceSelected = (choice: "original" | "processed"): void => {
    const originalOption = document.getElementById("choice-original");
    const processedOption = document.getElementById("choice-processed");

    originalOption?.classList.remove("camera-choice-option--selected");
    processedOption?.classList.remove("camera-choice-option--selected");

    if (choice === "original") {
      this.capturedPhotoDataUrl = this.originalPhotoDataUrl;
      originalOption?.classList.add("camera-choice-option--selected");
    } else {
      this.capturedPhotoDataUrl = this.processedPhotoDataUrl;
      processedOption?.classList.add("camera-choice-option--selected");
    }
  };

  public onCameraRetake = (): void => {
    const liveView = document.getElementById("camera-live-view");
    const previewView = document.getElementById("camera-preview-view");
    const capturedPreview = document.getElementById("camera-captured-preview");
    const choiceUI = document.getElementById("camera-photo-choice");
    const removeBgBtn = document.getElementById("remove-bg-btn") as HTMLButtonElement | null;

    if (liveView) liveView.style.display = "block";
    if (previewView) previewView.style.display = "none";
    if (capturedPreview) capturedPreview.style.display = "block";
    if (choiceUI) choiceUI.style.display = "none";
    if (removeBgBtn) removeBgBtn.removeAttribute("disabled");
    this.capturedPhotoDataUrl = "";
    this.originalPhotoDataUrl = "";
    this.processedPhotoDataUrl = "";
  };

  public onCameraUsePhoto = (): void => {
    if (!this.capturedPhotoDataUrl) return;

    const img = new Image();
    img.onload = () => {
      this.wizardState.portrait = {
        file: null,
        dataUrl: this.capturedPhotoDataUrl,
        width: img.width,
        height: img.height
      };
      this.stopCameraStream();
      this.showFinalPreview(this.capturedPhotoDataUrl);
      this.enablePortraitNext();
    };
    img.src = this.capturedPhotoDataUrl;
  };

  public onCameraBackToMode = (): void => {
    this.stopCameraStream();
    this.showPortraitModeSelect();
  };

  private async processPortraitPhoto(blob: Blob): Promise<string> {
    // Remove background using AI model
    const resultBlob = await removeBackground(blob);

    // Load the transparent-background result as an image
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = reject;
      image.src = URL.createObjectURL(resultBlob);
    });

    // Calculate 3:4 portrait crop dimensions
    const targetRatio = 3 / 4;
    const srcRatio = img.width / img.height;

    let cropW: number, cropH: number, cropX: number, cropY: number;
    if (srcRatio > targetRatio) {
      // Source is wider — crop width
      cropH = img.height;
      cropW = Math.round(img.height * targetRatio);
      cropX = Math.round((img.width - cropW) / 2);
      cropY = 0;
    } else {
      // Source is taller — crop height
      cropW = img.width;
      cropH = Math.round(img.width / targetRatio);
      cropX = 0;
      cropY = Math.round((img.height - cropH) / 2);
    }

    // Create white canvas at cropped portrait dimensions
    const canvas = document.createElement("canvas");
    canvas.width = cropW;
    canvas.height = cropH;
    const ctx = canvas.getContext("2d")!;

    // Fill with white background
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, cropW, cropH);

    // Draw the transparent image centered/cropped
    ctx.drawImage(img, cropX, cropY, cropW, cropH, 0, 0, cropW, cropH);

    URL.revokeObjectURL(img.src);

    return canvas.toDataURL("image/jpeg", 0.92);
  }

  private stopCameraStream(): void {
    if (this.cameraStream) {
      this.cameraStream.getTracks().forEach(track => track.stop());
      this.cameraStream = null;
    }
    const video = document.getElementById("camera-video") as HTMLVideoElement | null;
    if (video) {
      video.srcObject = null;
    }
  }

  // File upload handling
  private handlePortraitFile(file: File): void {
    this.hidePortraitError();

    // Validate MIME type
    if (file.type !== "image/png" && file.type !== "image/jpeg") {
      this.showPortraitError("Please upload a PNG or JPEG image.");
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      this.showPortraitError("File size must be less than 10MB.");
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target?.result as string;
      if (!dataUrl) return;

      const img = new Image();
      img.onload = () => {
        this.wizardState.portrait = {
          file: file,
          dataUrl: dataUrl,
          width: img.width,
          height: img.height
        };

        this.showUploadPreview(dataUrl);
        this.enablePortraitNext();
      };
      img.src = dataUrl;
    };
    reader.readAsDataURL(file);
  }

  private showUploadPreview(dataUrl: string): void {
    const placeholder = document.getElementById("portrait-dropzone-placeholder");
    const preview = document.getElementById("portrait-upload-preview") as HTMLImageElement | null;
    const actions = document.getElementById("portrait-upload-actions");

    if (placeholder) placeholder.style.display = "none";
    if (preview) {
      preview.src = dataUrl;
      preview.style.display = "block";
    }
    if (actions) actions.style.display = "flex";
  }

  private showFinalPreview(dataUrl: string): void {
    this.hideAllPortraitModes();
    const finalPreview = document.getElementById("portrait-final-preview");
    const finalImg = document.getElementById("portrait-final-img") as HTMLImageElement | null;

    if (finalPreview) finalPreview.style.display = "block";
    if (finalImg) finalImg.src = dataUrl;
  }

  public clearPortrait = (): void => {
    this.wizardState.portrait = null;

    // Reset upload UI
    const placeholder = document.getElementById("portrait-dropzone-placeholder");
    const preview = document.getElementById("portrait-upload-preview") as HTMLImageElement | null;
    const actions = document.getElementById("portrait-upload-actions");
    const fileInput = document.getElementById("portrait-file-input") as HTMLInputElement | null;

    if (placeholder) placeholder.style.display = "flex";
    if (preview) {
      preview.src = "";
      preview.style.display = "none";
    }
    if (actions) actions.style.display = "none";
    if (fileInput) fileInput.value = "";

    this.disablePortraitNext();
    this.hidePortraitError();
  };

  public changePortraitPhoto = (): void => {
    this.wizardState.portrait = null;
    this.capturedPhotoDataUrl = "";
    this.originalPhotoDataUrl = "";
    this.processedPhotoDataUrl = "";
    this.disablePortraitNext();

    // Reset upload UI
    const placeholder = document.getElementById("portrait-dropzone-placeholder");
    const preview = document.getElementById("portrait-upload-preview") as HTMLImageElement | null;
    const actions = document.getElementById("portrait-upload-actions");
    const fileInput = document.getElementById("portrait-file-input") as HTMLInputElement | null;

    if (placeholder) placeholder.style.display = "flex";
    if (preview) {
      preview.src = "";
      preview.style.display = "none";
    }
    if (actions) actions.style.display = "none";
    if (fileInput) fileInput.value = "";

    this.showPortraitModeSelect();
  };

  private enablePortraitNext(): void {
    const btn = document.getElementById("portrait-next-btn") as HTMLButtonElement | null;
    if (btn) btn.removeAttribute("disabled");
  }

  private disablePortraitNext(): void {
    const btn = document.getElementById("portrait-next-btn") as HTMLButtonElement | null;
    if (btn) btn.setAttribute("disabled", "true");
  }

  private showPortraitError(msg: string): void {
    const errorEl = document.getElementById("portrait-error");
    if (errorEl) {
      errorEl.textContent = msg;
      errorEl.style.display = "block";
    }
  }

  private hidePortraitError(): void {
    const errorEl = document.getElementById("portrait-error");
    if (errorEl) {
      errorEl.style.display = "none";
    }
  }

  // =============================================
  // APPLICATION FORM
  // =============================================

  private validateForm(): boolean {
    let valid = true;

    const familyName = (document.getElementById("field-family-name") as HTMLInputElement | null);
    const givenName = (document.getElementById("field-given-name") as HTMLInputElement | null);
    const email = (document.getElementById("field-email") as HTMLInputElement | null);
    const errorFamily = document.getElementById("error-family-name");
    const errorGiven = document.getElementById("error-given-name");
    const errorEmail = document.getElementById("error-email");

    // Reset error states
    if (familyName) familyName.classList.remove("form-input--error");
    if (givenName) givenName.classList.remove("form-input--error");
    if (email) email.classList.remove("form-input--error");
    if (errorFamily) errorFamily.style.display = "none";
    if (errorGiven) errorGiven.style.display = "none";
    if (errorEmail) errorEmail.style.display = "none";

    // Validate family name
    if (!familyName || familyName.value.trim() === "") {
      if (familyName) familyName.classList.add("form-input--error");
      if (errorFamily) errorFamily.style.display = "block";
      valid = false;
    }

    // Validate given name
    if (!givenName || givenName.value.trim() === "") {
      if (givenName) givenName.classList.add("form-input--error");
      if (errorGiven) errorGiven.style.display = "block";
      valid = false;
    }

    // Validate email
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email || email.value.trim() === "" || !emailPattern.test(email.value.trim())) {
      if (email) email.classList.add("form-input--error");
      if (errorEmail) errorEmail.style.display = "block";
      valid = false;
    }

    return valid;
  }

  private collectFormData(): void {
    const getValue = (id: string): string => {
      const el = document.getElementById(id) as HTMLInputElement | null;
      return el ? el.value.trim() : "";
    };

    this.wizardState.formData = {
      familyNameHebrew: getValue("field-family-name"),
      givenNameHebrew: getValue("field-given-name"),
      email: getValue("field-email"),
      previousLicenseNumber: getValue("field-prev-license"),
      licenseExpirationDate: getValue("field-license-expiry"),
      paymentCode: getValue("field-payment-code"),
      paymentDate: getValue("field-payment-date")
    };
  }

  private submitApplication(): void {
    const submitBtn = document.getElementById("form-submit-btn") as HTMLButtonElement | null;
    if (submitBtn) {
      submitBtn.setAttribute("disabled", "true");
      submitBtn.textContent = "Submitting...";
    }

    // Build submission payload
    const payload = {
      portrait: this.wizardState.portrait ? this.wizardState.portrait.dataUrl : null,
      faceTecVerification: {
        completed: this.wizardState.faceTecCompleted,
        externalDatabaseRefID: SampleAppController.demonstrationExternalDatabaseRefID
      },
      application: this.wizardState.formData
    };

    console.log("FaceTec SampleApp: Application payload:", payload);

    // Simulate submission delay (production: POST to backend)
    setTimeout(() => {
      if (submitBtn) {
        submitBtn.removeAttribute("disabled");
        submitBtn.textContent = "Submit Application";
      }

      // Populate confirmation screen with applicant details
      const greetingEl = document.getElementById("confirmation-greeting");
      const emailMsgEl = document.getElementById("confirmation-email-message");
      const { givenNameHebrew, familyNameHebrew, email } = this.wizardState.formData;

      if (greetingEl) {
        greetingEl.innerHTML =
          "Thank you <strong>" + familyNameHebrew + " " + givenNameHebrew + "</strong> for submitting the application";
      }
      if (emailMsgEl) {
        emailMsgEl.textContent = "An email has been sent to " + email + " with more information about your application.";
      }

      this.transitionToStep(WizardStep.CONFIRMATION);
    }, 1500);
  }
}

window.onload = (): void => {
  document.body.style.transition = "opacity 0.6s ease";
  document.body.style.opacity = "1";

  (window as any).sampleAppController = new SampleAppController();
};
